import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Calendar,
  ChevronLeft,
  Image,
  LayoutDashboard,
  MapPin,
  Megaphone,
  Settings,
  Trophy,
  Users,
  UserCircle,
  Volleyball,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import config from '@/config';
import { ROUTES } from '@/constants';
import { ROUTE_PERMISSIONS } from '@/constants/permissions';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/context';

const navGroups = [
  {
    label: 'Overview',
    items: [{ label: 'Dashboard', path: ROUTES.DASHBOARD, icon: LayoutDashboard, permission: ROUTE_PERMISSIONS.dashboard }],
  },
  {
    label: 'Tournament',
    items: [{ label: 'Tournaments', path: ROUTES.TOURNAMENTS, icon: Trophy, permission: ROUTE_PERMISSIONS.tournaments }],
  },
  {
    label: 'Competition',
    items: [
      { label: 'Teams', path: ROUTES.TEAMS, icon: Users, permission: ROUTE_PERMISSIONS.teams },
      { label: 'Players', path: ROUTES.PLAYERS, icon: UserCircle, permission: ROUTE_PERMISSIONS.players },
      { label: 'Matches', path: ROUTES.MATCHES, icon: Calendar, permission: ROUTE_PERMISSIONS.matches },
      { label: 'Grounds', path: ROUTES.GROUNDS, icon: MapPin, permission: ROUTE_PERMISSIONS.grounds },
    ],
  },
  {
    label: 'Content',
    items: [
      { label: 'Gallery', path: ROUTES.GALLERY, icon: Image, permission: ROUTE_PERMISSIONS.gallery },
      { label: 'Sponsors', path: ROUTES.SPONSORS, icon: Megaphone, permission: ROUTE_PERMISSIONS.sponsors },
    ],
  },
  {
    label: 'Insights',
    items: [{ label: 'Reports', path: ROUTES.REPORTS, icon: BarChart3, permission: ROUTE_PERMISSIONS.reports }],
  },
];

function AdminSidebar({
  collapsed = false,
  onCollapse,
  onNavigate,
  showCollapse = true,
  isDrawer = false,
  className,
}) {
  const location = useLocation();
  const { user } = useAuth();
  const { canAccessRoute } = usePermissions();
  const asideRef = useRef(null);
  const navRef = useRef(null);
  const [indicator, setIndicator] = useState(null);

  const visibleGroups = useMemo(() => {
    const groups =
      user?.role === 'scorer'
        ? [
            {
              label: 'Scoring',
              items: [
                { label: 'My Matches', path: ROUTES.MATCHES, icon: Calendar, permission: ROUTE_PERMISSIONS.matches },
              ],
            },
          ]
        : navGroups;

    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => canAccessRoute(item.permission)),
      }))
      .filter((group) => group.items.length > 0);
  }, [canAccessRoute, user?.role]);

  const showSettings = user?.role !== 'scorer' && canAccessRoute(ROUTE_PERMISSIONS.settings);

  const isActive = (path) =>
    location.pathname === path ||
    (path !== ROUTES.DASHBOARD && location.pathname.startsWith(`${path}/`));

  const linkClass = (path) =>
    cn(
      'relative flex min-h-12 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
      isActive(path)
        ? 'bg-primary-500/30 text-white shadow-sm ring-1 ring-white/10'
        : 'text-white/70 hover:bg-white/5 hover:text-white'
    );

  const updateIndicator = useCallback(() => {
    const aside = asideRef.current;
    if (!aside) return;

    const activeLink = aside.querySelector('[data-sidebar-active="true"]');
    if (!activeLink) {
      setIndicator(null);
      return;
    }

    const asideRect = aside.getBoundingClientRect();
    const linkRect = activeLink.getBoundingClientRect();

    setIndicator({
      top: linkRect.top - asideRect.top + linkRect.height / 2,
      left: linkRect.left - asideRect.left,
    });
  }, []);

  useEffect(() => {
    const nav = navRef.current;
    const activeLink = nav?.querySelector('[data-sidebar-active="true"]');

    if (nav && activeLink) {
      const navRect = nav.getBoundingClientRect();
      const linkRect = activeLink.getBoundingClientRect();
      const isFullyVisible =
        linkRect.top >= navRect.top + 2 && linkRect.bottom <= navRect.bottom - 2;

      if (!isFullyVisible) {
        activeLink.scrollIntoView({ block: 'nearest', behavior: 'instant' });
      }
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(updateIndicator);
    });
  }, [location.pathname, collapsed, updateIndicator, isDrawer, visibleGroups]);

  const handleNavigate = () => {
    onNavigate?.();
  };

  useEffect(() => {
    const aside = asideRef.current;
    const nav = navRef.current;
    if (!aside) return;

    const handleUpdate = () => {
      requestAnimationFrame(updateIndicator);
    };

    const resizeObserver = new ResizeObserver(handleUpdate);
    resizeObserver.observe(aside);

    nav?.addEventListener('scroll', handleUpdate, { passive: true });
    window.addEventListener('resize', handleUpdate);

    return () => {
      resizeObserver.disconnect();
      nav?.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [updateIndicator]);

  useEffect(() => {
    const aside = asideRef.current;
    if (!aside) return;

    const handleTransitionEnd = (event) => {
      if (event.propertyName === 'width') {
        updateIndicator();
      }
    };

    aside.addEventListener('transitionend', handleTransitionEnd);
    return () => aside.removeEventListener('transitionend', handleTransitionEnd);
  }, [updateIndicator]);

  return (
    <aside
      ref={asideRef}
      className={cn(
        'relative flex h-full min-h-0 flex-col overflow-hidden bg-primary-800 text-white',
        !isDrawer && 'shrink-0 transition-all duration-300',
        !isDrawer && (collapsed ? 'w-[var(--sidebar-collapsed)]' : 'w-[var(--sidebar-width)]'),
        isDrawer && 'w-full',
        className
      )}
    >
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute z-10 h-6 w-1 -translate-y-1/2 rounded-r-full bg-accent-cyan shadow-glow"
        initial={false}
        animate={{
          top: indicator?.top ?? 0,
          left: indicator?.left ?? 0,
          opacity: indicator ? 1 : 0,
        }}
        transition={{ type: 'spring', stiffness: 500, damping: 40, mass: 0.8 }}
      />
      <div
        className={cn(
          'flex shrink-0 items-center gap-3 border-b border-white/10 px-4',
          isDrawer ? 'h-14' : 'h-[var(--header-height)]'
        )}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-500">
          <Volleyball className="h-5 w-5" />
        </div>
        {(!collapsed || isDrawer) && (
          <div className="min-w-0">
            <p className="truncate font-display text-lg font-bold">{config.appName}</p>
            <p className="text-xs text-white/50">
              {user?.role === 'scorer' ? 'Scorer Panel' : 'Admin Panel'}
            </p>
          </div>
        )}
      </div>

      <nav
        ref={navRef}
        aria-label="Admin navigation"
        className="sidebar-scroll min-h-0 flex-1 scroll-smooth px-2 py-3 md:px-3"
      >
        <div className={cn('flex flex-col', collapsed && !isDrawer ? 'gap-1.5' : 'gap-5')}>
          {visibleGroups.map((group, groupIndex) => (
            <div
              key={group.label}
              className={cn(groupIndex > 0 && (!collapsed || isDrawer) && 'pt-0.5')}
            >
              {(!collapsed || isDrawer) && (
                <p className="mb-2.5 px-3 text-overline uppercase tracking-wide text-white/40">
                  {group.label}
                </p>
              )}
              {collapsed && !isDrawer && groupIndex > 0 && (
                <div className="mx-3 mb-1.5 border-t border-white/10" aria-hidden="true" />
              )}
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        title={collapsed && !isDrawer ? item.label : undefined}
                        onClick={handleNavigate}
                        data-sidebar-active={active ? 'true' : undefined}
                        className={linkClass(item.path)}
                      >
                        <Icon className="h-5 w-5 shrink-0" />
                        {(!collapsed || isDrawer) && <span className="truncate">{item.label}</span>}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </nav>

      {showSettings && (
        <div className="mt-auto shrink-0 border-t border-white/10 px-2 pb-3 pt-2 md:px-3">
          <Link
            to={ROUTES.SETTINGS}
            title={collapsed && !isDrawer ? 'Settings' : undefined}
            onClick={handleNavigate}
            data-sidebar-active={isActive(ROUTES.SETTINGS) ? 'true' : undefined}
            className={linkClass(ROUTES.SETTINGS)}
          >
            <Settings className="h-5 w-5 shrink-0" />
            {(!collapsed || isDrawer) && <span className="truncate">Settings</span>}
          </Link>

          {showCollapse && (
            <>
              <div className="mx-3 my-2 border-t border-white/5" aria-hidden="true" />

              <button
                type="button"
                onClick={onCollapse}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                className={cn(
                  'flex w-full items-center rounded-lg border border-white/10 bg-white/[0.03] text-sm font-medium text-white/60 transition-colors',
                  'hover:border-white/20 hover:bg-white/[0.07] hover:text-white',
                  'active:bg-white/10',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/30',
                  collapsed ? 'justify-center px-0 py-2.5' : 'justify-between px-3 py-2.5'
                )}
              >
                {!collapsed && <span className="truncate">Collapse</span>}
                <ChevronLeft
                  className={cn('h-4 w-4 shrink-0 transition-transform duration-300', collapsed && 'rotate-180')}
                />
              </button>
            </>
          )}
        </div>
      )}
    </aside>
  );
}

export default AdminSidebar;
